import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Paper, Typography, InputAdornment, Autocomplete, Alert, Snackbar, Stack } from "@mui/material";
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { selectUserData, selectUserToken, setDisplayErrorSnackBar } from '../../../../../features/userSlice';
import { ManualExpenseEndPointLogic } from '../../../../../logic/Finance/ProviderExpense/ManualExpenseEndpointLogic';
import SimpleBackdrop from '../../../../BackDrop/BackDrop';
import { NumericFormat } from 'react-number-format';
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from 'dayjs';
import { useManualExpenseFileUpload } from '../../../../../customHooks/useManualExpenseFileUpload';
import FileUploadManualExpense from './FileUploadManualExpense';
import { useManualExpenseValidation } from '../../../../../customHooks/useManualExpenseValidation';

const AddNewExpense = ({ open, handleClose }: any) => {
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const userData = useSelector(selectUserData);
  const [sourceUID, setSourceUID] = useState("");
  const [assetSharedUID, setAssetSharedUID] = useState("");
  const [date, setDate] = useState("");
  const [impressions, setImpressions] = useState(0);
  const [currencyCode, setCurrencyCode] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [clicks, setClicks] = useState(0);
  const [amount, setAmount] = useState("");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [searchParams] = useSearchParams();
  const { errors, validate, validateAll, resetErrors } = useManualExpenseValidation();

  const currency = useSelector((state: any) => state.manualExpense.currencies);
  const allTrafficSources = useSelector((state: any) => state.manualExpense.allTrafficSources);
  const selected = searchParams.get("selected");

  const siteNames = userData?.data?.website_permissions?.map((permission: any) => {
    const key = Object.keys(permission)[0];
    return {
      site: permission[key],
      key: key,
    };
  });

  const { uploadedFiles, handleFileUpload, setUploadedFiles, handleRemoveFile, handleFileDrop, isDragging, setIsDragging, uploadFilesManualExpense } = useManualExpenseFileUpload(assetSharedUID, "create_page");

  const handleCancel = () => {
    handleClose();
    setUploadedFiles([]); // reset uploaded files
    resetErrors(); // Reset validation errors when modal is cancelled
  };

  useEffect(() => {
    if (open) {
      ManualExpenseEndPointLogic.getTrafficSource(oauthTokenHeader);
      ManualExpenseEndPointLogic.getCurrency(oauthTokenHeader, dispatch);
      resetErrors(); // Reset validation errors when modal opens
    }
    // eslint-disable-next-line
  }, [open, oauthTokenHeader]);

  const handleAlertClose = () => {
    setAlertOpen(false); // Close Snackbar
  };

  useEffect(() => {
    if (alertOpen) {
      const timer = setTimeout(() => {
        setAlertOpen(false);
      }, 3000);
      return () => clearTimeout(timer); // Cleanup timer when component unmounts or alertOpen changes
    }
  }, [alertOpen]);

  const handleSave = async () => {
    const data = {
      website_shared_uid: assetSharedUID,
      currency: currencyCode,
      traffic_source: sourceUID,
      date: date,
      impressions: Number(impressions),
      clicks: Number(clicks),
      expenses: Number(amount),
      comment: comment,
    };
    const isValid = validateAll({ impressions, clicks, amount, comment });
    if (!isValid) {
      setAlertOpen(true); // show warning alert
      return; // prevent submission
    }
    try {
      setLoading(true);
      const res = await ManualExpenseEndPointLogic.createNewExpense(oauthTokenHeader, data);
      if (res?.reason !== "Created.") {
        setAlertOpen(true);
        setSnackbarMessage("Error: " + res?.reason);
        setSnackbarOpen(true);
      }
      if (uploadedFiles.length > 0) {
        await uploadFilesManualExpense(res?.data);
      }
      if (res?.reason === "Created.") {
        await ManualExpenseEndPointLogic.getManualExpenseData(oauthTokenHeader, dispatch);
        dispatch(setDisplayErrorSnackBar({ message: "Expense has been created successfully.", type: "success", }));
      }

    } catch (error: any) {
      const errorMessage = typeof error === "string" ? error : error instanceof Error ? error.message : "An unexpected error occurred.";
      dispatch(setDisplayErrorSnackBar({ message: `${errorMessage}`, type: "error", }));
    } finally {
      setLoading(false);
      handleClose();
      setUploadedFiles([]); // reset uploaded files
      resetErrors(); // Reset validation errors
      setAssetSharedUID("");
      setSourceUID("");
      setDate("");
      setImpressions(0);
      setClicks(0);
      setAmount("");
      setComment("");
      if (selected && selected.length > 0) {
        navigate(`/finance/providers_expenses/manual_expenses?selected=${selected}`);
      } else {
        navigate("/finance/providers_expenses/manual_expenses");
      }
    }
  }

  const renderSnackbar = () => {
    return (
      <Snackbar
        sx={{ zIndex: "9999999" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarMessage.includes("Error") ? "error" : "success"}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  };

  return (
    <>
      {renderSnackbar()}
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Dialog open={open} aria-labelledby="scroll-dialog-title" aria-describedby="scroll-dialog-description" PaperProps={{ sx: { width: "900px", maxWidth: "900px", overflowY: "auto" } }}>
            <Paper sx={{ padding: "24px 24px 20px 24px", maxHeight: "80vh", overflowY: "auto", }} ref={dialogContentRef}>
              {alertOpen && (
                <Alert
                  onClose={handleAlertClose}
                  severity="warning"
                  sx={{ width: "100%" }}
                >
                  All mandatory fields are required!
                </Alert>
              )}
              <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
                Add New Expense
              </DialogTitle>
              <Typography sx={{ marginBottom: "16px" }}>Details</Typography>
              <DialogContent sx={{ padding: "8px 0px" }}>
                <Stack flexDirection="row" gap="16px">
                  <Autocomplete
                    options={siteNames}
                    getOptionLabel={(option: any) => option?.site?.ui_site_name}
                    onChange={(_: unknown, option: any) =>
                      setAssetSharedUID(option?.key)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Related To"
                        placeholder="Choose Asset from the list."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start"></InputAdornment>
                          ),
                        }}
                        color="secondary"
                        required
                      />
                    )}
                    size='small'
                    sx={{ width: "300px" }}
                  />
                  <Autocomplete
                    options={allTrafficSources}
                    getOptionLabel={(option: any) => option.traffic_source_name}
                    onChange={(_, option: any) =>
                      setSourceUID(option?.shared_uid)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Related To"
                        placeholder="Choose Asset from the list."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start"></InputAdornment>
                          ),
                        }}
                        color="secondary"
                        required
                      />
                    )}
                    size='small'
                    sx={{ width: "300px" }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DatePicker"]} sx={{ paddingTop: "0", overflow: "visible" }}>
                      <DatePicker
                        className="brandDataDatePicker"
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            size: "small",
                            color: "secondary",
                            required: true,
                          },
                        }}
                        sx={{ width: "300px" }}
                        label="Pick a date"
                        onChange={(date) => {
                          if (date) {
                            const formattedDate =
                              dayjs(date).format("YYYY-MM-DD");
                            setDate(formattedDate);
                          }
                        }}
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                </Stack>
              </DialogContent>
              <DialogContent sx={{ padding: "0", display: "flex", flexDirection: "row", gap: "16px", flexWrap: "wrap" }}>
                <NumericFormat
                  customInput={TextField}
                  fullWidth
                  size="small"
                  color="secondary"
                  sx={{ mt: "16px", mb: "16px", padding: "0", width: "49%" }}
                  id="outlined-helperText"
                  label="Impressions"
                  defaultValue=""
                  thousandSeparator={true}
                  prefix=""
                  required
                  decimalScale={0}
                  fixedDecimalScale={false}
                  allowNegative={true}
                  error={Boolean(errors.impressions)}
                  helperText={errors.impressions}
                  onValueChange={(values) => {
                    const { value }: any = values;
                    setImpressions(value);
                    validate("impressions", value);
                  }}
                />
                <NumericFormat
                  customInput={TextField}
                  fullWidth
                  size="small"
                  color="secondary"
                  sx={{ mt: "16px", mb: "16px", padding: "0", width: "49%" }}
                  id="outlined-helperText"
                  label="Clicks"
                  defaultValue=""
                  required
                  thousandSeparator={true}
                  prefix=""
                  decimalScale={0}
                  fixedDecimalScale={false}
                  allowNegative={true}
                  error={Boolean(errors.clicks)}
                  helperText={errors.clicks}
                  onValueChange={(values) => {
                    const { value }: any = values;
                    setClicks(value);
                    validate("clicks", value);
                  }}
                />
              </DialogContent>
              <DialogContent sx={{ padding: "0" }}>
                <Stack flexDirection="row" gap="16px">
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    size="small"
                    color="secondary"
                    sx={{ mt: "16px", mb: "16px", padding: "0", width: "49%" }}
                    id="outlined-helperText"
                    label="Amount"
                    defaultValue=""
                    required
                    thousandSeparator={true}
                    prefix={currencySymbol ? `${currencySymbol} ` : ""}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    allowNegative={true}
                    error={Boolean(errors.amount)}
                    helperText={errors.amount}
                    onValueChange={(values) => {
                      const { value }: any = values;
                      setAmount(value);
                      validate("amount", value);
                    }}
                  />
                  <Autocomplete
                    options={currency || []}
                    getOptionLabel={(option: any) =>
                      option ? `${option.currency_symbol} ${option.currency_name}` : ""
                    }
                    onChange={(_, option: any) => {
                      setCurrencyCode(option?.shared_uid);
                      setCurrencySymbol(option?.currency_symbol);
                    }}
                    renderOption={(props, option: any) => (
                      <li {...props}>
                        {/* Add the currency symbol next to the currency name */}
                        <span style={{ marginRight: 8 }}>
                          {option?.currency_symbol}
                        </span>
                        {option?.currency_name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Currency"
                        placeholder="Choose Currency from the list."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start"></InputAdornment>
                          ),
                        }}
                        color="secondary"
                        required
                      />
                    )}
                    size="small"
                    sx={{ width: "49%", marginTop: "16px", marginBottom: "16px" }}
                  />
                </Stack>
              </DialogContent>
              <Typography
                sx={{
                  marginBottom: "16px",
                  marginTop: "8px",
                  fontSize: "12px",
                  lineHeight: "20px",
                  color: "#00000099",
                }}
              >
                Fields marked with * are mandatory.
              </Typography>
              <Typography mt="16px" mb="8px">
                Expense Description
              </Typography>
              <TextField
                id="outlined-multiline-flexible"
                label="Statement" // Set your label here
                placeholder="Add your description here" // Set your placeholder here
                multiline
                color="secondary"
                size="medium"
                required
                maxRows={4}
                error={Boolean(errors.comment)}
                helperText={errors.comment}
                sx={{
                  width: "852px",
                }}
                onChange={(e) => {
                  setComment(e.target.value);
                  validate("comment", e.target.value);
                }}
              />
              <FileUploadManualExpense isDragging={isDragging} setIsDragging={setIsDragging} handleFileDrop={handleFileDrop} handleFileUpload={handleFileUpload} uploadedFiles={uploadedFiles} handleRemoveFile={handleRemoveFile} />
            </Paper>
            <Paper sx={{ padding: "16px 24px 20px 24px" }}>
              <DialogActions sx={{ padding: "16px 0 0 0" }}>
                <Button onClick={handleCancel} color="secondary">Cancel</Button>
                <Button
                  disabled={!assetSharedUID || !sourceUID || !date || !impressions || !clicks || !amount || !currencyCode || !comment || Object.values(errors).some(error => error !== "")}
                  onClick={handleSave}
                  color="secondary" variant="contained">
                  Add Expense{" "}
                  <CheckOutlinedIcon
                    fontSize="small"
                    sx={{ marginLeft: "8px" }}
                  />
                </Button>
              </DialogActions>
            </Paper>
          </Dialog>
        </>
      )}
    </>
  )
}

export default AddNewExpense
